import { EventBus } from '@repo/utils';
import { action, computed, makeObservable, observable } from 'mobx';

import { agent } from 'src/api/agent';
import { PlanVersionType } from 'src/store/comparison/types';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import type { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';
import {
  FULL_ACCESS_ROLE,
  GOPLAN_DRILLING_CARPET_WELLS_ACCESS_EDIT,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
} from 'src/store/roles-store/roles';
import type { RolesStore } from 'src/store/roles-store/roles-store';

import { WellsTableTemplateStore } from './features/wells-table/wells-table-templates/wells-table-template.store';
import { WellsTableTemplatesApi } from './features/wells-table/wells-table-templates/wells-table-templates.api';

type TEventMap = {
  export: {
    type: 'export';
  };
  deleteWells: {
    type: 'deleteWells';
  };
};

export class WellsPageStore {
  readonly eventBus = new EventBus<TEventMap>();
  private readonly rolesStore: RolesStore;
  private readonly planVersionsService: PlanVersionsService;
  tableTemplatesStore: WellsTableTemplateStore;

  @observable isTableSettingsOpen = false;
  @observable isTableCheckboxesOn = false;
  @observable isTableTemplatesOpen = false;
  @observable isExportTemplatesOpen = false;

  @observable isDrilledWellsChecked = true;
  @observable isPlannedWellsChecked = true;
  @observable isUnplannedWellsChecked = true;

  @observable startDate: number | null = null;
  @observable endDate: number | null = null;

  @observable isExporting = false;

  @observable isDeleteButtonDisabled: boolean | { disabled: boolean; description: string } = true;

  constructor(notifications: NotificationsStore, rolesStore: RolesStore, planVersionsService: PlanVersionsService) {
    this.tableTemplatesStore = new WellsTableTemplateStore(
      new WellsTableTemplatesApi(agent),
      notifications,
      rolesStore,
    );

    this.rolesStore = rolesStore;
    this.planVersionsService = planVersionsService;

    makeObservable(this);
  }

  @action.bound
  setIsDrilledWellsChecked(isDrilledWellsChecked: boolean): void {
    this.isDrilledWellsChecked = isDrilledWellsChecked;
  }

  @action.bound
  setIsPlannedWellsChecked(isPlannedWellsChecked: boolean): void {
    this.isPlannedWellsChecked = isPlannedWellsChecked;
  }

  @action.bound
  setIsUnplannedWellsChecked(isUnplannedWellsChecked: boolean): void {
    this.isUnplannedWellsChecked = isUnplannedWellsChecked;
  }

  @action.bound
  toggleTableCheckboxes(): void {
    this.isTableCheckboxesOn = !this.isTableCheckboxesOn;
  }

  @action.bound
  setIsTableSettingsOpen(isTableSettingsOpen: boolean): void {
    this.isTableSettingsOpen = isTableSettingsOpen;
  }

  @action.bound
  setIsTableTemplatesOpen(isTableTemplatesOpen: boolean): void {
    this.isTableTemplatesOpen = isTableTemplatesOpen;
  }

  @action.bound
  setIsExportTemplatesOpen(isExportTemplatesOpen: boolean): void {
    this.isExportTemplatesOpen = isExportTemplatesOpen;
  }

  @action.bound
  private validateDates(startDate: number): void {
    if (startDate > (this.endDate ?? 0)) {
      this.endDate = null;
    }
  }

  @action.bound
  setStartDate(startDate: number | null): void {
    this.startDate = startDate;

    if (startDate) {
      this.validateDates(startDate);
    }
  }

  @action.bound
  setEndDate(endDate: number | null): void {
    this.endDate = endDate;
  }

  @action.bound
  setIsExporting(isExporting: boolean): void {
    this.isExporting = isExporting;
  }

  @action.bound
  setIsDeleteButtonDisabled(isDeleteButtonDisabled: boolean | { disabled: boolean; description: string }): void {
    this.isDeleteButtonDisabled = isDeleteButtonDisabled;
  }

  @computed
  get isEditingAvailable(): boolean {
    if (this.planVersionsService.currentVersion?.data.isArchive) {
      return this.rolesStore.hasAccess([
        FULL_ACCESS_ROLE,
        GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
        GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
      ]);
    }

    if (this.planVersionsService.currentVersion?.data.versionType === PlanVersionType.current) {
      return false;
    }

    return this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_WELLS_ACCESS_EDIT]);
  }

  onDeleteWells = () => {
    this.eventBus.publish('deleteWells', { type: 'deleteWells' });
  };

  init = () => {
    this.tableTemplatesStore.loadTemplates();
  };
}
